import type { CommunityMember } from "@/react/types/CommunityMember";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { usePunditUserContext } from "@circle-react/contexts";

const getInitials = (firstName?: string, lastName?: string): string => {
  if (!firstName && !lastName) return "";

  const initials = [];
  if (firstName) initials.push([...firstName][0]);
  if (lastName) initials.push([...lastName][0]);

  return initials.join("").toUpperCase();
};

interface CommunityPreviewData {
  displayName: string;
  customerJourneyStage?: string;
  achieveWithCircle?: string;
  buildingBlocks: string[];
  userInitials: string;
  fullName: string;
  email?: string;
  avatar_url?: string;
  shouldAnimateCustomerJourneyStage: boolean;
  shouldAnimateAchieveWithCircle: boolean;
}

export const useCommunityPreviewData = (
  communityName: string,
  customBuildingBlocks?: string[],
  animateField?: "customerJourneyStage" | "achieveWithCircle",
): CommunityPreviewData => {
  const { currentUser } = useCurrentCommunityContext() as {
    currentUser: CommunityMember;
  };
  const { currentCommunity } = usePunditUserContext();

  const blocks = customBuildingBlocks ?? [];

  const firstName = currentUser?.first_name ?? "";
  const lastName = currentUser?.last_name ?? "";
  const userInitials = getInitials(firstName, lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(" ");

  const customerJourneyStage =
    currentCommunity?.sign_up_flow_responses?.customer_journey_stage;
  const achieveWithCircle =
    currentCommunity?.sign_up_flow_responses?.achieve_with_circle;

  const displayName = communityName || currentCommunity?.name || "";

  const shouldAnimateCustomerJourneyStage =
    animateField === "customerJourneyStage";
  const shouldAnimateAchieveWithCircle = animateField === "achieveWithCircle";

  return {
    displayName,
    customerJourneyStage,
    achieveWithCircle,
    buildingBlocks: blocks,
    userInitials,
    fullName,
    email: currentUser?.email,
    avatar_url: currentUser?.avatar_url,
    shouldAnimateCustomerJourneyStage,
    shouldAnimateAchieveWithCircle,
  };
};
